import { useRef, useState } from "react";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { FieldHint } from "../shared/FieldHint";
import { api, formatApiErrorDetail } from "../../lib/api";

const PRODUCTS = [{ value: "funding", label: "Business Funding" }, { value: "trust", label: "Trust & Asset Protection" }];

export function LeadAssignmentPanel({ managers, onAssigned }) {
  const [form, setForm] = useState({ manager_id: "", name: "", product_interest: "funding", phone: "", email: "", deal_value: "" });
  const [csvManagerId, setCsvManagerId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const assignManual = async (e) => {
    e.preventDefault();
    if (!form.manager_id || !form.name.trim()) { toast.error("Manager and lead name are required"); return; }
    setSubmitting(true);
    try {
      await api.post("/admin/lead-assignment/manual", { ...form, deal_value: parseFloat(form.deal_value) || 0 });
      toast.success("Lead assigned to manager");
      setForm({ manager_id: form.manager_id, name: "", product_interest: "funding", phone: "", email: "", deal_value: "" });
      onAssigned();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const uploadCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!csvManagerId) { toast.error("Choose a manager for this CSV first"); return; }
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await api.post(`/admin/lead-assignment/csv?manager_id=${csvManagerId}`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(data.message);
      onAssigned();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <div className="bg-igc-surface border border-igc-gold/15 p-5 mb-8" data-testid="lead-assignment-panel">
      <h2 className="font-serif text-xl text-igc-cream tracking-tight mb-4 flex items-center">
        Lead Assignment
        <FieldHint text="Push a lead straight into a manager's Lead Inbox, one at a time or in bulk with a CSV." testId="lead-assignment-hint" />
      </h2>

      <form onSubmit={assignManual} className="flex items-end gap-2 flex-wrap mb-4">
        <Select value={form.manager_id} onValueChange={(v) => setForm({ ...form, manager_id: v })}>
          <SelectTrigger data-testid="lead-assignment-manager-select" title="Choose which manager's team this lead goes to — it'll land in their Lead Inbox for them to assign to a rep." className="w-44 h-9 bg-igc-surfaceLight border-igc-gold/20 text-igc-cream text-xs"><SelectValue placeholder="Assign to manager" /></SelectTrigger>
          <SelectContent className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
            {managers.map((m) => <SelectItem key={m.id} value={m.id}>{m.code} — {m.display_name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input data-testid="lead-assignment-name-input" placeholder="Lead name" title="The prospect's full name." value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream w-40 h-9 text-xs" />
        <Select value={form.product_interest} onValueChange={(v) => setForm({ ...form, product_interest: v })}>
          <SelectTrigger data-testid="lead-assignment-product-select" title="Which product this lead is interested in — Business Funding or Trust & Asset Protection." className="w-48 h-9 bg-igc-surfaceLight border-igc-gold/20 text-igc-cream text-xs"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
            {PRODUCTS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input data-testid="lead-assignment-phone-input" placeholder="Phone" title="The prospect's phone number." value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream w-32 h-9 text-xs" />
        <Input data-testid="lead-assignment-email-input" placeholder="Email" title="The prospect's email address." value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream w-44 h-9 text-xs" />
        <div className="flex items-center gap-1">
          <Input data-testid="lead-assignment-deal-value-input" type="number" min="0" placeholder="Est. deal value" value={form.deal_value} onChange={(e) => setForm({ ...form, deal_value: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream w-32 h-9 text-xs" />
          <FieldHint text="Your best guess at how much this deal could be worth. Helps Wes see projected revenue before it closes." testId="lead-assignment-deal-value-hint" />
        </div>
        <Button data-testid="lead-assignment-submit-button" type="submit" disabled={submitting} title="Creates this lead and drops it directly into the chosen manager's Lead Inbox." className="h-9 text-xs bg-igc-gold hover:bg-igc-goldHover text-black">Assign Lead</Button>
      </form>

      <div className="flex items-center gap-2 flex-wrap">
        <Select value={csvManagerId} onValueChange={setCsvManagerId}>
          <SelectTrigger data-testid="lead-assignment-csv-manager-select" title="Choose which manager's team every lead in the CSV file below should be assigned to." className="w-44 h-9 bg-igc-surfaceLight border-igc-gold/20 text-igc-cream text-xs"><SelectValue placeholder="CSV → manager" /></SelectTrigger>
          <SelectContent className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
            {managers.map((m) => <SelectItem key={m.id} value={m.id}>{m.code} — {m.display_name}</SelectItem>)}
          </SelectContent>
        </Select>
        <input ref={fileInputRef} data-testid="lead-assignment-csv-input" type="file" accept=".csv" onChange={uploadCsv} title="Upload a CSV file to bulk-assign many leads at once to the manager selected on the left." className="text-xs text-igc-muted" />
        <span className="text-igc-muted text-[10px]">CSV columns: name, phone, email, product_interest (funding/trust)</span>
      </div>
    </div>
  );
}
